import {
  getPlanDisplay,
  type PaywallPlan,
} from '@/components/v1/paywallPricing'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

interface Props {
  animationDelayMs: number
  currency: string
  isPopular: boolean
  isSelected: boolean
  onSelect: () => void
  paywallPlan: PaywallPlan
}

export default function PaywallPlanCard({
  animationDelayMs,
  currency,
  isPopular,
  isSelected,
  onSelect,
  paywallPlan,
}: Props) {
  const { t } = useTranslation()

  const { anchorPerDay, isOneTime, perDay, periodPrice, savePercent } =
    getPlanDisplay({ currency, paywallPlan })

  const chargedPrice = paywallPlan.intro ?? paywallPlan.full
  const recurring = chargedPrice.recurring

  const planLabel = recurring
    ? `${recurring.interval_count}-${t(
        `pages.checkout.plans.${recurring.interval}`,
        { count: recurring.interval_count },
      )}`
    : t('pages.checkout.plans.oneTimePurchase')

  return (
    <button
      aria-pressed={isSelected}
      className={twMerge(
        'animate-fade-up relative w-full rounded-2xl border text-left transition-[border-color,background-color,transform] duration-200 active:scale-[0.99]',
        // The radio circle stays on the plan cards, unlike the question
        // options: a pricing selector has to show the alternatives it is
        // choosing between, so the affordance is carrying real information.
        isSelected
          ? 'border-hw-primary bg-hw-primary-soft'
          : 'border-hw-line bg-white',
        isPopular && 'mt-3',
      )}
      onClick={onSelect}
      style={{ animationDelay: `${animationDelayMs}ms` }}
      type="button"
    >
      {isPopular && (
        <span className="bg-hw-primary absolute -top-3 left-4 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide text-white uppercase">
          {t('pages.checkout.plans.mostPopular')}
        </span>
      )}

      <div className="flex items-center gap-3 p-4">
        <span
          className={twMerge(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200',
            isSelected
              ? 'border-hw-primary bg-hw-primary'
              : 'border-hw-line bg-white',
          )}
        >
          {isSelected && (
            <CheckIcon className="animate-check-pop h-4 w-4 text-white" />
          )}
        </span>

        <div className="grow">
          <p className="text-hw-ink text-[17px] font-bold">{planLabel}</p>

          <div className="mt-0.5 flex items-center gap-2">
            <p className="text-hw-muted text-sm">{periodPrice}</p>

            {savePercent > 0 && (
              <span className="bg-hw-green-soft text-hw-green rounded-md px-1.5 py-0.5 text-[11px] font-bold">
                {t('pages.checkout.v1.save', { percent: savePercent })}
              </span>
            )}
          </div>
        </div>

        {!isOneTime && (
          <div className="shrink-0 text-right">
            {anchorPerDay && (
              <p className="text-hw-muted text-xs line-through">
                {anchorPerDay}
              </p>
            )}

            <p
              className={twMerge(
                'text-[26px] leading-none font-extrabold tracking-tight',
                isSelected ? 'text-hw-primary' : 'text-hw-ink',
              )}
            >
              {perDay}
            </p>

            <p className="text-hw-muted text-[11px] font-medium">
              {t('pages.checkout.plans.perDay')}
            </p>
          </div>
        )}
      </div>
    </button>
  )
}
